import { useState, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { registerUser, loginUser, sendPasswordReset, sendMagicLink, checkEmailRegistered } from '../utils/supabaseAuth';
import { getQuizQuestions, getCategories, CATEGORY_META } from '../utils/matching';
import { getNetworkDefaultLang } from '../utils/i18n';
import { QUIZ_ICONS } from '../components/icons';

function getBeeWords(tr){return[tr('auth.bee_word1'),tr('auth.bee_word2'),tr('auth.bee_word3'),tr('auth.bee_word4'),tr('auth.bee_word5'),tr('auth.bee_word6')];}

// ── Quiz Step (step 5 of onboarding) ────────────────────────
function QuizStep({ onComplete, onSkip, initialAnswers={}, initialIdx=0, lang='en' }) {
  const [qIdx, setQIdx]         = useState(initialIdx);
  const [answers, setAnswers]   = useState(initialAnswers);
  const [animating, setAnimating] = useState(false);

  const q        = getQuizQuestions(lang)[qIdx] || getQuizQuestions(lang)[0];
  const answered = answers[q.id] != null;
  const progress = qIdx / getQuizQuestions(lang).length;

  // Build categories dynamically from getQuizQuestions(lang) so it never goes stale
  const CATEGORIES_DYN = Object.entries(
    getQuizQuestions(lang).reduce((acc, q, i) => {
      if (!acc[q.category]) acc[q.category] = { id:q.category, label: getCategories(lang)[q.category]?.label || q.category, qs:[] };
      acc[q.category].qs.push(i);
      return acc;
    }, {})
  ).map(([,v]) => v);
  const currentCat = CATEGORIES_DYN.find(c => c.qs.includes(qIdx));

  const handleSelect = (value) => {
    if (animating) return;
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);
    setAnimating(true);
    setTimeout(() => {
      setAnimating(false);
      if (qIdx < getQuizQuestions(lang).length - 1) {
        setQIdx(i => i + 1);
      } else {
        onComplete(newAnswers);
      }
    }, 280);
  };

  return (
    <div style={{ background:'var(--black)', display:'flex', flexDirection:'column', minHeight:'100%' }}>
      {/* Top progress */}
      <div style={{ height:2, background:'var(--border)' }}>
        <div style={{ height:'100%', width:`${progress * 100}%`, background:'var(--gold)', transition:'width 0.3s ease' }}/>
      </div>
      <div style={{ display:'flex', flexDirection:'column', padding:'1.8rem 1.5rem 1.5rem', maxWidth:520, margin:'0 auto', width:'100%' }}>
        {/* Category + counter */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <div style={{
              width:6, height:6, borderRadius:'50%',
              background: currentCat ? (CATEGORY_META[currentCat.id]?.color || 'var(--gold)') : 'var(--gold)',
            }}/>
            <span style={{ fontSize:'0.65rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)' }}>
              {currentCat?.label}
            </span>
          </div>
          <span style={{ fontSize:'0.7rem', color:'var(--muted2)', letterSpacing:'0.05em' }}>
            {qIdx + 1} / {getQuizQuestions(lang).length}
          </span>
        </div>

        {/* Question */}
        <div style={{ opacity: animating ? 0 : 1, transition:'opacity 0.18s', marginBottom:'1.5rem' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:'1.2rem' }}>
            <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              {(() => { const QI = QUIZ_ICONS[q.id]; return QI ? <QI size={26} color='var(--gold)'/> : null; })()}
            </div>
          </div>
          <h2 style={{
            fontFamily:"'Cormorant Garamond',serif",
            fontSize:'1.65rem', fontWeight:400, lineHeight:1.2,
            marginBottom:'2rem', color:'var(--white)',
          }}>{q.question}</h2>

          <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
            {q.options.map(opt => {
              const selected = answers[q.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  style={{
                    padding:'0.95rem 1.1rem',
                    background: selected ? 'var(--gold-pale)' : 'var(--dark)',
                    border: `1px solid ${selected ? 'var(--gold)' : 'var(--border)'}`,
                    color: selected ? 'var(--gold)' : 'var(--white)',
                    fontFamily:'Outfit', fontSize:'0.88rem', lineHeight:1.5,
                    cursor:'pointer', textAlign:'left', transition:'all 0.15s',
                  }}
                >{opt.label}</button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'0.5rem', borderTop:'1px solid var(--border)' }}>
          {qIdx > 0 ? (
            <button
              onClick={() => setQIdx(i => i - 1)}
              style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'0.78rem', fontFamily:'Outfit', display:'flex', alignItems:'center', gap:'0.3rem' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              Previous
            </button>
          ) : <span/>}
          {onSkip && (
            <button
              onClick={() => onSkip(answers, qIdx)}
              style={{ background:'none', border:'none', color:'var(--muted2)', cursor:'pointer', fontSize:'0.72rem', fontFamily:'Outfit', letterSpacing:'0.04em' }}
            >
              Skip for now →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Auth ────────────────────────────────────────────────
// ── Age calculation ───────────────────────────────────────────────────────
function calcAgeFromDOB(day, month, year) {
  const d = parseInt(day), m = parseInt(month), y = parseInt(year);
  if (!d || !m || !y || y < 1900 || y > new Date().getFullYear()) return null;
  const today = new Date();
  const birthDate = new Date(y, m - 1, d);
  if (isNaN(birthDate.getTime())) return null;
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
}

// ── AgeGate — DOB picker with under-18 block ─────────────────────────────
function AgeGate({ form, set, tr }) {
  const age = calcAgeFromDOB(form.dobDay, form.dobMonth, form.dobYear);
  const tooYoung = age !== null && age < 18;
  const valid    = age !== null && age >= 18;

  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

  // Sync age field for rest of onboarding
  if (valid && String(age) !== String(form.age)) {
    set('age', String(age));
  }

  return (
    <div>
      {/* DOB selectors */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 1fr', gap:'0.5rem', marginBottom:'1rem' }}>
        {/* Day */}
        <div>
          <div style={{ fontSize:'0.6rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.3rem' }}>{tr('auth.age_day')}</div>
          <select
            value={form.dobDay || ''}
            onChange={e => set('dobDay', e.target.value)}
            style={{ width:'100%', padding:'0.65rem 0.5rem', background:'var(--dark)', border:`1px solid ${tooYoung ? 'var(--red, #E07070)' : valid ? 'rgba(78,201,148,0.5)' : 'var(--border)'}`, borderRadius:'var(--r-md)', color: form.dobDay ? 'var(--white)' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}>
            <option value="">--</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        {/* Month */}
        <div>
          <div style={{ fontSize:'0.6rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.3rem' }}>{tr('auth.age_month')}</div>
          <select
            value={form.dobMonth || ''}
            onChange={e => set('dobMonth', e.target.value)}
            style={{ width:'100%', padding:'0.65rem 0.5rem', background:'var(--dark)', border:`1px solid ${tooYoung ? 'var(--red, #E07070)' : valid ? 'rgba(78,201,148,0.5)' : 'var(--border)'}`, borderRadius:'var(--r-md)', color: form.dobMonth ? 'var(--white)' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}>
            <option value="">---</option>
            {months.map((mo, i) => (
              <option key={i+1} value={i+1}>{mo}</option>
            ))}
          </select>
        </div>
        {/* Year */}
        <div>
          <div style={{ fontSize:'0.6rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.3rem' }}>{tr('auth.age_year')}</div>
          <select
            value={form.dobYear || ''}
            onChange={e => set('dobYear', e.target.value)}
            style={{ width:'100%', padding:'0.65rem 0.5rem', background:'var(--dark)', border:`1px solid ${tooYoung ? 'var(--red, #E07070)' : valid ? 'rgba(78,201,148,0.5)' : 'var(--border)'}`, borderRadius:'var(--r-md)', color: form.dobYear ? 'var(--white)' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.85rem', outline:'none' }}>
            <option value="">----</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Under-18 hard block */}
      {tooYoung && (
        <div style={{ padding:'1rem', background:'rgba(224,112,112,0.08)', border:'1px solid rgba(224,112,112,0.35)', borderRadius:'var(--r-md)', textAlign:'center' }}>
          <div style={{ fontSize:'1.5rem', color:'#E07070', marginBottom:'0.5rem' }}>✕</div>
          <div style={{ fontSize:'0.9rem', fontWeight:600, color:'#E07070', marginBottom:'0.3rem' }}>
            {tr('auth.age_too_young')}
          </div>
          <div style={{ fontSize:'0.72rem', color:'var(--muted)', lineHeight:1.6 }}>
            {tr('auth.age_too_young_sub')}
          </div>
        </div>
      )}

      {/* Valid age confirmation */}
      {valid && (
        <div style={{ padding:'0.75rem 1rem', background:'rgba(78,201,148,0.08)', border:'1px solid rgba(78,201,148,0.3)', borderRadius:'var(--r-md)', display:'flex', alignItems:'center', gap:'0.6rem' }}>
          <span style={{ fontSize:'1.1rem' }}>✓</span>
          <div>
            <div style={{ fontSize:'0.82rem', color:'#4EC994', fontWeight:600 }}>
              {tr('auth.age_valid')} — {age} years old
            </div>
            <div style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.1rem' }}>
              {tr('auth.age_legal')}
            </div>
          </div>
        </div>
      )}

      {/* Why we verify */}
      {!tooYoung && !valid && (
        <div style={{ padding:'0.65rem 0.9rem', background:'rgba(255,255,255,0.02)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginTop:'0.5rem' }}>
          <div style={{ fontSize:'0.68rem', color:'var(--muted)', lineHeight:1.6 }}>
            {tr('auth.age_why_text')}
          </div>
        </div>
      )}
    </div>
  );
}


export default function Auth({ onLogin }) {
  const tr = useTr();
  const { partialQuizAnswers, quizResumeIdx, savePartialQuiz, quizDone, lang, setLang } = useApp();
  const fr = lang === 'fr';
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const submitLogin = async () => {
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);
    const { error } = await loginUser({ email: form.email, password: form.password });
    setAuthLoading(false);
    if (error) { setAuthError(error); return; }
    onLogin();
  };
  const [mode, setMode] = useState('landing');
  const [magicSent, setMagicSent] = useState(false);
  const [noAccountFound, setNoAccountFound] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ email:'', password:'', name:'', age:'', dobDay:'', dobMonth:'', dobYear:'', gender:'', seeking:[], network:'', inviteCode:'' });
  const [inviteChecked,  setInviteChecked]  = useState(false);
  const [inviteValid,    setInviteValid]    = useState(false);
  const [inviteChecking, setInviteChecking] = useState(false);
  const [inviteError,    setInviteError]    = useState('');
  const [isFounding,   setIsFounding]   = useState(false);
  const [inviteData,   setInviteData]   = useState(null); // { code, created_by, senderName, plan_grant, is_founding }
  const [inviteChoice, setInviteChoice] = useState(null); // null | 'new'
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Waitlist request form state
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistName,     setWaitlistName]     = useState('');
  const [waitlistMsg,      setWaitlistMsg]      = useState('');
  const [waitlistSent,     setWaitlistSent]     = useState(false);
  const [waitlistSending,  setWaitlistSending]  = useState(false);

  // Detect invite link on mount — show account-check screen first
  useEffect(() => {
    const fromSession = sessionStorage.getItem('pendingInviteCode');
    // Invite code comes via /invite/:code path — App.js stores it in sessionStorage before navigating here
    const code = fromSession || '';
    if (!code) return;
    sessionStorage.removeItem('pendingInviteCode');
    set('inviteCode', code);
    setMode('invite_landing');
    // Load invite details (anon policy allows this)
    import('../utils/supabase').then(async ({ supabase }) => {
      const { data } = await supabase
        .from('invitations')
        .select('id, used_count, max_uses, expires_at, is_founding, plan_grant, created_by')
        .eq('code', code.trim())
        .maybeSingle();
      if (!data || data.used_count >= data.max_uses) {
        setInviteError(tr('invite.invalid_code') || 'Invitation invalide ou déjà utilisée.');
        return;
      }
      setInviteValid(true);
      setIsFounding(!!data.is_founding || data.plan_grant === 'founder');
      if (data.plan_grant) window._beeInvitePlanGrant = data.plan_grant;
      let senderName = null;
      if (data.created_by) {
        const { data: sender } = await supabase.from('user_core').select('name').eq('id', data.created_by).maybeSingle();
        senderName = sender?.name || null;
      }
      setInviteData({ code, created_by: data.created_by, senderName, plan_grant: data.plan_grant, is_founding: !!data.is_founding });
    });
  }, []);

  const validateInviteCode = async (code) => {
    if (!code?.trim()) return;
    setInviteChecking(true);
    setInviteError('');
    try {
      const { supabase } = await import('../utils/supabase');
      const { data } = await supabase
        .from('invitations')
        .select('id, used_count, max_uses, expires_at, is_founding, plan_grant')
        .eq('code', code.trim())
        .maybeSingle();
      if (!data || data.used_count >= data.max_uses) {
        setInviteError(tr('invite.invalid_code') || 'Invalid or already used code.');
        setInviteValid(false);
      } else if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setInviteError(tr('invite.expired_code') || 'This code has expired.');
        setInviteValid(false);
      } else {
        setInviteValid(true);
        setIsFounding(!!data.is_founding || data.plan_grant === 'founder');
        setInviteError('');
        // store plan grant for registration
        if (data.plan_grant) window._beeInvitePlanGrant = data.plan_grant;
      }
    } catch {
      // If DB unreachable, allow to proceed (will be validated server-side)
      setInviteValid(true);
    }
    setInviteChecking(false);
    setInviteChecked(true);
  };

  const ONBOARDING = [
    // ── Step 0 — Invite code ───────────────────────────────────────────────
    {
      q: tr('invite.step_title') || 'Enter your invitation code',
      sub: tr('invite.step_sub') || 'BeeLove is currently invite-only. Enter the code you received.',
      content: (
        <div>
          <div style={{ position:'relative' }}>
            <input
              className="input"
              style={{ textAlign:'center', fontSize:'0.95rem', letterSpacing:'0.05em', fontFamily:'monospace' }}
              placeholder={tr('invite.code_placeholder') || 'Paste your invitation link code'}
              value={form.inviteCode}
              onChange={e => { set('inviteCode', e.target.value.trim()); setInviteChecked(false); setInviteValid(false); setInviteError(''); }}
              onBlur={() => validateInviteCode(form.inviteCode)}
              onKeyDown={e => e.key === 'Enter' && validateInviteCode(form.inviteCode)}
              autoFocus
            />
          </div>
          {inviteChecking && <p style={{ textAlign:'center', fontSize:'0.78rem', color:'var(--muted)', marginTop:'0.5rem' }}>Checking…</p>}
          {inviteChecked && inviteValid && (
            <div style={{ textAlign:'center', marginTop:'0.6rem' }}>
              {isFounding
                ? <p style={{ fontSize:'0.82rem', color:'var(--gold)', fontWeight:600 }}>✦ Founding member code — welcome to the first wave.</p>
                : <p style={{ fontSize:'0.82rem', color:'var(--green,#4EC994)' }}>✓ Valid code — welcome to BeeLove.</p>}
            </div>
          )}
          {inviteError && <p style={{ textAlign:'center', fontSize:'0.78rem', color:'var(--red,#E07070)', marginTop:'0.5rem' }}>{inviteError}</p>}
        </div>
      ),
      ok: inviteValid,
    },
    // ── Step 1 — Name ──────────────────────────────────────────────────────
    {
      q: tr('auth.step_name_q'),
      sub: tr('auth.step_name_sub'),
      content: (
        <input className="input" style={{ textAlign:'center', fontSize:'1.2rem', letterSpacing:'0.05em' }}
          placeholder={tr('auth.step_name_ph')} value={form.name} onChange={e => set('name', e.target.value)} autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && form.name.length > 1) setStep(n => n + 1); }}/>
      ),
      ok: form.name.length > 1,
    },
    {
      q: tr('auth.age_gate_title'),
      sub: tr('auth.age_gate_sub'),
      content: <AgeGate form={form} set={set} tr={tr}/>,
      ok: (() => {
        const age = calcAgeFromDOB(form.dobDay, form.dobMonth, form.dobYear);
        return age !== null && age >= 18;
      })(),
    },
    {
      q: tr('auth.step_gender_q2'),
      sub: tr('auth.step_gender_sub'),
      content: (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {[['man',tr('auth.gender_man')],['woman',tr('auth.gender_woman')],['nonbinary',tr('auth.gender_nb')]].map(([v, l]) => (
            <button key={v} onClick={() => set('gender', v)} style={{
              padding:'0.95rem', border:`1px solid ${form.gender===v?'var(--gold)':'var(--border)'}`,
              background: form.gender===v ? 'var(--gold-pale)' : 'var(--dark)',
              color: form.gender===v ? 'var(--gold)' : 'var(--muted)',
              fontFamily:'Cormorant Garamond', fontSize:'1.05rem', fontStyle:'italic',
              cursor:'pointer', transition:'all 0.15s', textAlign:'left', paddingLeft:'1.2rem',
            }}>{l}</button>
          ))}
        </div>
      ),
      ok: !!form.gender,
    },
    {
      q: tr('auth.step_seek_q'),
      sub: tr('auth.step_seek_sub'),
      content: (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {[['man',tr('auth.seek_man')],['woman',tr('auth.seek_woman')],['nonbinary',tr('auth.seek_nb')],['all',tr('auth.seek_all')]].map(([v, l]) => {
            const sel = v==='all' ? form.seeking.length===3 : form.seeking.includes(v);
            return (
              <button key={v} onClick={() => {
                if (v==='all') set('seeking', ['man','woman','nonbinary']);
                else {
                  const next = sel ? form.seeking.filter(x=>x!==v) : [...form.seeking,v];
                  set('seeking', next);
                }
              }} style={{
                padding:'0.95rem', border:`1px solid ${sel?'var(--gold)':'var(--border)'}`,
                background: sel ? 'var(--gold-pale)' : 'var(--dark)',
                color: sel ? 'var(--gold)' : 'var(--muted)',
                fontFamily:'Cormorant Garamond', fontSize:'1.05rem', fontStyle:'italic',
                cursor:'pointer', transition:'all 0.15s', textAlign:'left', paddingLeft:'1.2rem',
              }}>{l}</button>
            );
          })}
        </div>
      ),
      ok: form.seeking.length > 0,
    },
    {
      q: tr('auth.step_network_q'),
      sub: tr('auth.step_network_sub'),
      content: (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {[
            { id:'ca_montreal', flag:'🍁', label:'Montréal',        desc:tr('auth.network_montreal_area'), active:true },
            { id:'fr_lorient',  flag:'⚓', label:'Lorient',          desc:tr('network.lorient_area'),       active:false, launchLabel: 'H2 2026' },
            { id:'fr_paris',    flag:'🗼', label:'Paris',            desc:tr('network.paris_area') || 'Paris & Île-de-France', active:false, launchLabel: 'H2 2026' },
            { id:'be_brussels', flag:'🧇', label:'Bruxelles',        desc:tr('network.brussels_area') || 'Bruxelles & environs',  active:false, launchLabel: 'H2 2026' },
          ].map(n => (
            <button key={n.id} onClick={() => { if (!n.active) return; set('network', n.id); const nl = getNetworkDefaultLang(n.id); if (nl) setLang(nl); }}
              style={{
                padding:'0.9rem 1.1rem', border:`1px solid ${form.network===n.id?'var(--gold)':'var(--border)'}`,
                background: form.network===n.id ? 'var(--gold-pale)' : 'var(--dark)',
                cursor: n.active ? 'pointer' : 'not-allowed',
                opacity: n.active ? 1 : 0.35,
                display:'flex', alignItems:'center', gap:'0.8rem', transition:'all 0.15s',
              }}>
              <span style={{ fontSize:'1.5rem' }}>{n.flag}</span>
              <div style={{ textAlign:'left', flex:1, minWidth:0 }}>
                <div style={{ fontFamily:'Outfit', fontSize:'0.95rem', fontWeight:600, color: form.network===n.id ? 'var(--gold)' : 'var(--white)', marginBottom:'0.1rem' }}>{n.label}</div>
                <div style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{n.desc}{!n.active && ` — ${n.launchLabel}`}</div>
              </div>
              {form.network===n.id && <svg style={{ marginLeft:'auto', flexShrink:0 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
              {!n.active && <span style={{ marginLeft:'auto', flexShrink:0, fontSize:'0.58rem', letterSpacing:'0.08em', color:'var(--muted)', border:'1px solid var(--border)', borderRadius:99, padding:'0.2rem 0.55rem', textTransform:'uppercase' }}>Soon</span>}
            </button>
          ))}
          <p style={{ fontSize:'0.65rem', color:'var(--muted)', textAlign:'center', marginTop:'0.3rem', lineHeight:1.5 }}>
            {tr('auth.network_connect')}<br/>{tr('auth.network_note2')}
          </p>
        </div>
      ),
      ok: !!form.network,
    },
  ];

  // Quiz interstitiel before discover
  if (mode === 'quiz') {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)' }}>
        {/* Quiz header */}
        <div style={{
          padding:'1.5rem 1.5rem 1rem',
          borderBottom:'1px solid var(--border)',
          textAlign:'center',
        }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', fontWeight:600 }}>
            <span style={{ color:'var(--gold)', fontStyle:'italic' }}>Bee</span>Love.me
          </div>
          <p style={{ fontSize:'0.78rem', color:'var(--muted)', marginTop:'0.3rem' }}>
            One last step — helps us find your best matches
          </p>
        </div>
        <QuizStep lang={lang||'en'}
          onComplete={async (answers) => {
            setAuthError('');
            setAuthLoading(true);
            const { error } = await registerUser({
              email:      form.email,
              password:   form.password,
              name:       form.name,
              age:        form.age,
              gender:     form.gender,
              seeking:    form.seeking,
              dobDay:     form.dobDay,
              dobMonth:   form.dobMonth,
              dobYear:    form.dobYear,
              networkId:  form.network || 'ca_montreal',
              planGrant:  window._beeInvitePlanGrant || null,
              isFounding: isFounding,
            });
            if (!error) {
              await saveQuizAnswers(answers);
              onLogin();
            } else {
              setAuthLoading(false);
              setAuthError(tr(error) || error);
              setMode('signin'); // fallback to sign-in
            }
          }}
          onSkip={(partialAnswers, idx) => { savePartialQuiz(partialAnswers, idx); onLogin(form); }}
          initialAnswers={partialQuizAnswers}
          initialIdx={quizResumeIdx}
         lang={lang||'en'}/>
      </div>
    );
  }

  if (mode === 'onboarding') {
    const s = ONBOARDING[step];
    const totalSteps = ONBOARDING.length;
    const advanceStep = () => {
      if (!s.ok) return;
      if (step < ONBOARDING.length - 1) setStep(n => n + 1);
      else if (quizDone || sessionStorage.getItem('pendingRateTarget')) onLogin(form);
      else setMode('quiz');
    };
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', gap:'2px', flexShrink:0 }}>
          {ONBOARDING.map((_, i) => (
            <div key={i} style={{ flex:1, height:2, background: i<=step ? 'var(--gold)' : 'var(--muted2)', transition:'background 0.3s' }}/>
          ))}
        </div>

        <div
          style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding:'2rem 1.5rem' }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) advanceStep(); }}
        >
          <div className="eyebrow" style={{ marginBottom:'0.6rem' }}>{tr('auth.step_of',{n:step+1,total:totalSteps})}</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:400, marginBottom:'0.4rem' }}>{s.q}</h2>
          <p style={{ color:'var(--muted)', fontSize:'0.85rem', marginBottom:'2rem' }}>{s.sub}</p>
          {s.content}
        </div>

        <div style={{ padding:'1.5rem', flexShrink:0, borderTop:'1px solid var(--border)' }}>
          <button
            className="btn btn-gold btn-lg btn-full"
            disabled={!s.ok}
            style={{ opacity: s.ok ? 1 : 0.4, marginBottom:'0.7rem' }}
            onClick={advanceStep}
          >
            {step < ONBOARDING.length - 1 ? 'Continue →' : (tr('auth.cta_start'))}
          </button>
          {step > 0 && (
            <button className="btn btn-ghost btn-full btn-sm" onClick={() => setStep(n => n - 1)}>{tr('common.back_btn')}</button>
          )}
        </div>
      </div>
    );
  }

  // ── Invite landing — account check + register vs rate choice ────────────
  if (mode === 'invite_landing') {
    const fr = lang === 'fr';
    const senderName = inviteData?.senderName;
    const planGrant  = inviteData?.plan_grant || 'honey';
    const monthsMap  = { founder:6, honey:3, gold:1 };
    const months     = monthsMap[planGrant] || 1;
    const monthLabel = fr ? `${months} mois` : `${months} month${months > 1 ? 's' : ''}`;
    const loading    = !inviteData && !inviteError;

    if (inviteError) return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', justifyContent:'center', padding:'2rem 1.5rem', textAlign:'center' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', color:'var(--gold)', marginBottom:'1rem', lineHeight:1 }}>✦</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', color:'var(--white)', marginBottom:'0.6rem' }}>
          {fr ? 'Invitation invalide' : 'Invalid invitation'}
        </h2>
        <p style={{ fontSize:'0.82rem', color:'var(--red,#E07070)', marginBottom:'1.5rem' }}>{inviteError}</p>
        <button className="btn btn-outline btn-md" onClick={() => setMode('landing')}>{fr ? '← Retour' : '← Back'}</button>
      </div>
    );

    if (loading) return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:'var(--muted)', fontSize:'0.85rem' }}>{fr ? 'Chargement…' : 'Loading…'}</p>
      </div>
    );

    // Sub-screen 2: no account — register or just rate
    if (inviteChoice === 'new') return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', padding:'2rem 1.5rem' }}>
        <button onClick={() => setInviteChoice(null)} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', textAlign:'left', marginBottom:'1.5rem', fontSize:'0.82rem', fontFamily:'Outfit' }}>← {fr ? 'Retour' : 'Back'}</button>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:400, marginBottom:'0.4rem' }}>
          {fr ? 'Que veux-tu faire ?' : 'What would you like to do?'}
        </h2>
        <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'2rem' }}>
          {senderName ? (fr ? `${senderName} t'a invité·e.` : `${senderName} invited you.`) : ''}
        </p>

        {/* Option 1 — Sign up */}
        <div style={{ background:'var(--gold-pale)', border:'1px solid var(--gold)', borderRadius:'var(--r-lg)', padding:'1.2rem', marginBottom:'0.8rem' }}>
          <p style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--gold)', margin:'0 0 0.3rem' }}>✦ {monthLabel} {fr ? 'offerts' : 'free'}</p>
          <p style={{ fontSize:'0.85rem', color:'var(--white)', fontWeight:600, margin:'0 0 0.3rem', fontFamily:"'Cormorant Garamond',serif" }}>
            {fr ? `Rejoindre BeeLove` : `Join BeeLove`}
          </p>
          <p style={{ fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.6, margin:'0 0 1rem' }}>
            {fr
              ? `Crée ton profil et profite de ${monthLabel} de fonctionnalités premium gratuitement.`
              : `Set up your profile and enjoy ${monthLabel} of premium features for free.`}
          </p>
          <button className="btn btn-gold btn-md btn-full" onClick={() => {
            if (inviteData?.created_by) sessionStorage.setItem('pendingRateTarget', inviteData.created_by);
            setMode('onboarding');
          }}>
            {fr ? `S'inscrire — ${monthLabel} offerts →` : `Sign up — ${monthLabel} free →`}
          </button>
        </div>

        {/* Option 2 — Just rate */}
        <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1.2rem' }}>
          <p style={{ fontSize:'0.85rem', color:'var(--white)', fontWeight:600, margin:'0 0 0.3rem', fontFamily:"'Cormorant Garamond',serif" }}>
            {fr ? `Aider ${senderName || 'mon ami·e'}` : `Help ${senderName || 'my friend'}`}
          </p>
          <p style={{ fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.6, margin:'0 0 1rem' }}>
            {fr
              ? `Réponds à quelques questions sur ${senderName || 'ton ami·e'} pour l'aider à trouver quelqu'un qui lui correspond vraiment.`
              : `Answer a few questions about ${senderName || 'your friend'} to help them find a truly compatible match.`}
          </p>
          <button className="btn btn-outline btn-md btn-full" onClick={() => {
            if (inviteData?.created_by) { import('../utils/navigate').then(({ navigate }) => navigate(`rate/${inviteData.created_by}`)); }
            else setMode('landing');
          }}>
            {fr ? `Répondre au questionnaire →` : `Answer the questionnaire →`}
          </button>
        </div>
      </div>
    );

    // Sub-screen 1: account check
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', justifyContent:'center', padding:'2rem 1.5rem' }}>
        <div style={{ textAlign:'center', marginBottom:'2rem' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', fontWeight:600, marginBottom:'0.5rem' }}>
            <span style={{ color:'var(--gold)', fontStyle:'italic' }}>Bee</span>Love.me
          </div>
          {senderName && (
            <p style={{ fontSize:'0.88rem', color:'var(--muted)', lineHeight:1.6 }}>
              {fr ? `${senderName} t'a envoyé une invitation.` : `${senderName} sent you an invitation.`}
            </p>
          )}
        </div>

        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:400, marginBottom:'0.4rem' }}>
          {fr ? 'Tu as déjà un compte ?' : 'Do you have an account?'}
        </h2>
        <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'1.5rem' }}>
          {fr ? 'Connecte-toi ou crée un compte pour profiter de ton invitation.' : 'Sign in or create an account to use your invitation.'}
        </p>

        <button className="btn btn-gold btn-lg btn-full" style={{ marginBottom:'0.6rem' }}
          onClick={() => { set('inviteCode', inviteData?.code || ''); setMode('email'); }}>
          {fr ? 'Oui — me connecter' : 'Yes — sign in'}
        </button>
        <button className="btn btn-outline btn-lg btn-full" onClick={() => setInviteChoice('new')}>
          {fr ? "Non, c'est ma première fois" : "No, this is my first time"}
        </button>
      </div>
    );
  }

  if (mode === 'landing') {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', alignItems:'center' }}>
        <style>{`@media(min-width:640px){.auth-hero{max-height:300px!important;}}`}</style>
        <div className="auth-wrap" style={{ width:'100%', display:'flex', flexDirection:'column', alignItems:'stretch' }}>
          <div className="auth-card" style={{ width:'100%', background:'var(--black)', display:'flex', flexDirection:'column' }}>

            {/* Hero image */}
            <div className="auth-hero" style={{ position:'relative', height:'40vh', minHeight:260, flexShrink:0 }}>
              <img
                src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800&q=85&auto=format&fit=crop"
                alt=""
                style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center top', filter:'brightness(0.5)' }}
              />
              <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 30%, var(--black) 100%)' }}/>
              <div style={{ position:'absolute', top:'1.8rem', left:0, right:0, textAlign:'center' }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', fontWeight:600, letterSpacing:'0.05em' }}>
                  <span style={{ color:'var(--gold)', fontStyle:'italic' }}>Bee</span>Love.me
                </div>
              </div>
              <div style={{ position:'absolute', bottom:'0.2rem', left:'1.5rem', right:'1.5rem' }}>
                <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.8rem', fontWeight:300, lineHeight:1.05, marginBottom:'0.8rem' }}>
                  {tr('landing.hero_dont_just')}<br/>
                  <em style={{ fontStyle:'italic', color:'var(--gold)' }}>Bee</em> {tr('landing.hero_bee_here')}<br/>
                  <span style={{ fontWeight:600 }}>{tr('landing.hero_bee_loved')}</span>
                </h1>
                <p style={{ color:'var(--muted)', fontSize:'0.9rem', fontWeight:300, lineHeight:1.6 }}>
                  {tr('landing.hero_sub')}
                </p>
              </div>
            </div>{/* /auth-hero */}

            {/* Bottom CTA */}
            <div style={{ padding:'1.5rem', background:'var(--black)', flexShrink:0 }}>
              <div style={{ display:'flex', gap:'0.5rem', overflowX:'auto', marginBottom:'1.5rem', paddingBottom:'2px' }}>
                {getBeeWords(tr).map((w, i) => (
                  <span key={i} style={{
                    flexShrink:0, padding:'0.25rem 0.7rem',
                    border:'1px solid var(--gold-border)',
                    fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic',
                    fontSize:'0.85rem', color:'var(--gold)', background:'var(--gold-pale)',
                  }}>{w}</span>
                ))}
              </div>

              {magicSent ? (
                /* ── Magic link sent confirmation ── */
                <div style={{ textAlign:'center', padding:'0.5rem 0 0.8rem' }}>
                  <div style={{ marginBottom:14 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(201,168,76,0.8)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/>
                    </svg>
                  </div>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--white)', margin:'0 0 6px' }}>
                    {fr ? 'Vérifie tes emails' : 'Check your inbox'}
                  </p>
                  <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6, margin:'0 0 16px' }}>
                    {fr ? `Lien envoyé à ` : `Link sent to `}
                    <strong style={{ color:'var(--white)' }}>{form.email}</strong>
                    {fr ? '. Clique dessus pour te connecter.' : '. Click it to sign in.'}
                  </p>
                  <button onClick={() => { setMagicSent(false); setAuthError(''); }}
                    style={{ background:'none', border:'none', color:'var(--muted)', fontSize:'0.78rem', cursor:'pointer', textDecoration:'underline' }}>
                    {fr ? 'Changer d\'adresse' : 'Use a different email'}
                  </button>
                  {process.env.NODE_ENV === 'development' && (
                    <a href="http://localhost:54324" target="_blank" rel="noreferrer"
                      style={{ display:'block', marginTop:12, fontSize:'0.72rem', color:'rgba(201,168,76,0.7)', textDecoration:'none', border:'1px solid rgba(201,168,76,0.25)', borderRadius:6, padding:'6px 12px' }}>
                      Dev — Open Inbucket →
                    </a>
                  )}
                </div>
              ) : noAccountFound ? (
                /* ── No account found ── */
                <div style={{ textAlign:'center', padding:'0.5rem 0 0.8rem' }}>
                  <div style={{ marginBottom:14 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                    </svg>
                  </div>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--white)', margin:'0 0 6px' }}>
                    {fr ? 'Aucun compte trouvé' : 'No account found'}
                  </p>
                  <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6, margin:'0 0 16px' }}>
                    {fr
                      ? <>Aucun compte n'est associé à <strong style={{ color:'var(--white)' }}>{form.email}</strong>. BeeLove est sur invitation — tu as besoin d'un lien d'invitation pour rejoindre.</>
                      : <>No account linked to <strong style={{ color:'var(--white)' }}>{form.email}</strong>. BeeLove is invite-only — you'll need an invitation link to join.</>
                    }
                  </p>
                  <button onClick={() => { setNoAccountFound(false); setAuthError(''); setShowWaitlistForm(false); setWaitlistSent(false); }}
                    style={{ background:'none', border:'none', color:'var(--muted)', fontSize:'0.78rem', cursor:'pointer', textDecoration:'underline', display:'block', margin:'0 auto' }}>
                    {fr ? 'Essayer une autre adresse' : 'Try a different email'}
                  </button>

                  {/* Waiting list request */}
                  {!waitlistSent ? (
                    showWaitlistForm ? (
                      <div style={{ marginTop:'1.2rem', textAlign:'left', borderTop:'1px solid var(--border)', paddingTop:'1.2rem' }}>
                        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--white)', marginBottom:'0.8rem' }}>
                          {fr ? 'Demander un accès' : 'Request access'}
                        </p>
                        <input
                          className="input"
                          placeholder={fr ? 'Ton prénom' : 'Your name'}
                          value={waitlistName}
                          onChange={e => setWaitlistName(e.target.value)}
                          style={{ marginBottom:'0.55rem' }}
                          autoFocus
                        />
                        <textarea
                          className="input"
                          placeholder={fr ? 'Message optionnel — pourquoi rejoindre BeeLove ?' : 'Optional message — why join BeeLove?'}
                          value={waitlistMsg}
                          onChange={e => setWaitlistMsg(e.target.value)}
                          rows={3}
                          style={{ resize:'vertical', marginBottom:'0.8rem', fontFamily:'Outfit', fontSize:'0.85rem' }}
                        />
                        <button
                          className="btn btn-gold btn-md btn-full"
                          disabled={waitlistSending || !waitlistName.trim()}
                          style={{ opacity: waitlistName.trim() ? 1 : 0.4, marginBottom:'0.5rem' }}
                          onClick={async () => {
                            setWaitlistSending(true);
                            const { supabase } = await import('../utils/supabase');
                            await supabase.from('waitlist').insert({
                              name: waitlistName.trim(),
                              email: form.email,
                              message: waitlistMsg.trim() || null,
                              status: 'pending',
                            });
                            setWaitlistSending(false);
                            setWaitlistSent(true);
                          }}
                        >
                          {waitlistSending ? '…' : (fr ? 'Envoyer ma demande' : 'Send my request')}
                        </button>
                        <button onClick={() => setShowWaitlistForm(false)}
                          style={{ background:'none', border:'none', color:'var(--muted)', fontSize:'0.75rem', cursor:'pointer', width:'100%', textAlign:'center' }}>
                          {fr ? '← Annuler' : '← Cancel'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowWaitlistForm(true)}
                        style={{ background:'none', border:'1px solid var(--border)', color:'var(--muted)', fontSize:'0.78rem', cursor:'pointer', marginTop:'1rem', padding:'0.5rem 1rem', width:'100%', fontFamily:'Outfit' }}
                      >
                        {fr ? "Pas encore invité·e ? Demander l'accès →" : "Not invited? Request access →"}
                      </button>
                    )
                  ) : (
                    <div style={{ marginTop:'1.2rem', padding:'0.9rem', background:'rgba(78,201,148,0.08)', border:'1px solid rgba(78,201,148,0.3)', borderRadius:'var(--r-md)', textAlign:'center' }}>
                      <div style={{ fontSize:'1.4rem', marginBottom:'0.4rem' }}>✓</div>
                      <p style={{ fontSize:'0.85rem', color:'#4EC994', fontWeight:600, margin:'0 0 0.25rem' }}>
                        {fr ? 'Demande envoyée !' : 'Request sent!'}
                      </p>
                      <p style={{ fontSize:'0.72rem', color:'var(--muted)', lineHeight:1.6, margin:0 }}>
                        {fr
                          ? 'On te contactera dès qu\'une place se libère.'
                          : "We'll reach out as soon as a spot opens up."}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* ── Magic link — primary ── */}
                  <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.8rem' }}>
                    <input
                      className="input"
                      type="email"
                      placeholder={fr ? 'Ton adresse e-mail' : 'Your email address'}
                      value={form.email}
                      onChange={e => { set('email', e.target.value); setNoAccountFound(false); }}
                      onKeyDown={async e => {
                        if (e.key !== 'Enter' || !form.email) return;
                        setAuthError(''); setNoAccountFound(false); setAuthLoading(true);
                        const { exists } = await checkEmailRegistered(form.email);
                        if (!exists) { setAuthLoading(false); setNoAccountFound(true); return; }
                        const { error } = await sendMagicLink(form.email);
                        setAuthLoading(false);
                        if (error) setAuthError(error); else setMagicSent(true);
                      }}
                      style={{ flex:1, marginBottom:0 }}
                    />
                    <button
                      className="btn btn-gold btn-md"
                      disabled={authLoading || !form.email}
                      onClick={async () => {
                        if (!form.email) return;
                        setAuthError(''); setNoAccountFound(false); setAuthLoading(true);
                        const { exists } = await checkEmailRegistered(form.email);
                        if (!exists) { setAuthLoading(false); setNoAccountFound(true); return; }
                        const { error } = await sendMagicLink(form.email);
                        setAuthLoading(false);
                        if (error) setAuthError(error); else setMagicSent(true);
                      }}
                      style={{ flexShrink:0, padding:'0 1.2rem' }}
                    >
                      {authLoading ? '…' : '→'}
                    </button>
                  </div>
                  {authError && (
                    <p style={{ color:'var(--red,#E07070)', fontSize:'0.8rem', marginBottom:'0.7rem', textAlign:'center' }}>
                      {tr(authError) || authError}
                    </p>
                  )}
                  <p style={{ fontSize:'0.68rem', color:'var(--muted)', textAlign:'center', margin:0, lineHeight:1.5 }}>
                    {fr ? 'Lien valide 24h — aucun mot de passe nécessaire.' : 'Link valid 24h — no password needed.'}
                  </p>
                </>
              )}

              <p style={{ textAlign:'center', fontSize:'0.68rem', color:'var(--muted2)', marginTop:'1rem', lineHeight:1.6 }}>
                {tr('auth.by_continuing')}{' '}
                <span style={{ color:'var(--muted)', borderBottom:'1px solid var(--muted2)', cursor:'pointer' }}>{tr('auth.terms_label')}</span>
                {' '}{tr('auth.and')}{' '}
                <span style={{ color:'var(--muted)', borderBottom:'1px solid var(--muted2)', cursor:'pointer' }}>{tr('auth.privacy_policy')}</span>.
              </p>
            </div>{/* /cta */}

          </div>{/* /auth-card */}
        </div>{/* /auth-wrap */}
      </div>
    );
  }

    // Email login
  return (
    <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', justifyContent:'center', padding:'2rem 1.5rem' }}>
      <button onClick={() => setMode('landing')} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', textAlign:'left', marginBottom:'1.5rem', display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Outfit', fontSize:'0.85rem' }}>
        ← Back
      </button>
      <div className="eyebrow" style={{ marginBottom:'0.4rem' }}>{tr('auth.welcome_back')}</div>
      <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:400, marginBottom:'2rem' }}>
        <em className="bee-word">Bee</em> yourself
      </h2>
      <input className="input" type="email" placeholder={tr('auth.email')} value={form.email} onChange={e => set('email', e.target.value)} style={{ marginBottom:'0.7rem' }}
        onKeyDown={e => e.key === 'Enter' && submitLogin()}/>
      <input className="input" type="password" placeholder={tr('auth.password')} value={form.password} onChange={e => set('password', e.target.value)} style={{ marginBottom:'1.2rem' }}
        onKeyDown={e => e.key === 'Enter' && submitLogin()}/>
      {authError && (
        <p style={{ color:'var(--red,#E07070)', fontSize:'0.85rem', textAlign:'center', marginBottom:'0.8rem' }}>
          {tr(authError) || authError}
        </p>
      )}
      <button
        className="btn btn-gold btn-lg btn-full"
        disabled={authLoading}
        onClick={submitLogin}
      >{authLoading ? '…' : tr('auth.sign_in_btn')}</button>
      <p style={{ textAlign:'center', fontSize:'0.82rem', color:'var(--muted)', marginTop:'1.2rem' }}>
      <button onClick={async () => {
          if (!form.email) { setAuthError(tr('auth.error_email_required')); return; }
          const { error } = await sendPasswordReset(form.email);
          setAuthError(error ? tr(error)||error : tr('auth.reset_sent'));
        }} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'0.75rem', display:'block', margin:'0 auto 0.8rem', textDecoration:'underline' }}>
          {tr('auth.forgot_password')}
        </button>
      {tr('auth.no_account')}{' '}
        <button onClick={() => setMode('onboarding')} style={{ background:'none', border:'none', color:'var(--gold)', fontFamily:'Cormorant Garamond', fontStyle:'italic', fontSize:'0.95rem', cursor:'pointer' }}>
          Bee Loved — join free →
        </button>
      </p>
    </div>
  );
}
